import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Phone, AlertTriangle, X, Check, Loader2 } from 'lucide-react';

const PhoneNumberAlert = () => {
  const [showAlert, setShowAlert] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkPhoneNumber();
  }, []);

  const checkPhoneNumber = async () => {
    try {
      const response = await api.getMyProfile();
      const user = response.data;
      // Show alert if phone number is missing or empty
      if (!user.phone_number || user.phone_number.trim() === '') {
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Failed to check phone number:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    // Basic validation
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setSaving(true);
    try {
      await api.updateMyPhone(phoneNumber);
      toast.success('Phone number updated successfully!');
      setShowAlert(false);
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone number');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !showAlert) return null;

  return (
    <>
      {/* Alert Banner */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-xl p-4 mb-6 shadow-lg animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-full">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">⚠️ Action Required: Update Your Phone Number</h3>
              <p className="text-white/90 text-sm">
                Your phone number is needed to match you with HR records for salary, attendance & benefits. Please update immediately!
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-white text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors shadow-md"
            data-testid="update-phone-btn"
          >
            <Phone className="h-5 w-5" />
            Update Now
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-full">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Update Phone Number</h2>
                    <p className="text-white/80 text-sm">Required for HR records</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold">Why is this required?</p>
                    <p className="mt-1">Your phone number will be used to match your profile with HR records for salary, attendance, and other employee benefits.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Your Phone Number *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">+91</span>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="9876543210"
                    className="w-full pl-14 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-lg font-medium"
                    maxLength={10}
                    autoFocus
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">Enter your 10-digit mobile number</p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Later
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-phone-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5" />
                      Save Phone Number
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PhoneNumberAlert;
